/**
 * Fase B — Shop Account UI. Eén bestand met alle shopaccount-pagina's voor
 * compacte routing. Subpaths:
 *   /shop-account/register
 *   /shop-account/login
 *   /shop-account              — dashboard (orderhistorie + profielsamenvatting)
 *   /shop-account/orders/{id}  — orderdetail
 *   /shop-account/profile      — profiel + adresboek
 *   /shop-account/verify       — verify-email landingspagina
 *   /shop-account/forgot-password
 *   /shop-account/reset-password
 *
 * Visueel: marineblauw 5999Cargo-stijl, scherpe hoeken, geen rounded-xl op
 * cards (alleen op buttons/inputs). Mobile-first.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Mail, Lock, User, Phone, AlertCircle, CheckCircle2, Loader2,
  Package, MapPin, LogOut, ShieldAlert, RefreshCw, ChevronRight, UserCog,
  MessageSquare, Bell, CreditCard,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useShopAuth, shopApi } from "../context/ShopAuthContext";
import { usePostcodeLookup } from "../lib/postcodeLookup";
import { AccountAccessPanel } from "../components/AccountAccessPanel";

const fmtEur = (v) => `€ ${Number(v || 0).toFixed(2)}`;

// ============================================================
// Layout
// ============================================================
function ShopAccountShell({ children, title, subtitle, testId = "shop-account-shell" }) {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4" data-testid={testId}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <Link to="/" data-testid="account-back-to-5999cargo" className="text-xs uppercase tracking-widest text-[#1a5193] hover:underline">
            ← Terug naar 5999Cargo
          </Link>
          <h1 className="font-chivo font-black text-3xl sm:text-4xl text-[#082c50] mt-2">{title}</h1>
          {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
        </div>
        {children}
      </div>
    </div>
  );
}

function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div className="bg-red-50 border border-red-200 p-3 text-sm text-red-800 flex items-start gap-2 mb-4" data-testid="shop-account-error">
      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

function readDetail(err) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return d?.msg || err?.message || "Er is iets misgegaan.";
}

// ============================================================
// Register
// ============================================================
export function RegisterPage() {
  const { register, customer } = useShopAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const prefillEmail = sp.get("email") || "";
  const returnTo = sp.get("returnTo") || "";
  const [form, setForm] = useState({ first_name: "", last_name: "", email: prefillEmail, phone: "", whatsapp: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (customer) return <Navigate to={returnTo || "/shop-account"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) return setError("Wachtwoord moet minimaal 8 tekens hebben.");
    if (!/[A-Za-z]/.test(form.password) || !/\d/.test(form.password))
      return setError("Wachtwoord moet minimaal 1 letter en 1 cijfer bevatten.");
    setSubmitting(true);
    try {
      // Use shopApi direct to capture email_status from response. Context's
      // `register` only returns the customer-dict so we hit endpoint here.
      const r = await shopApi.post("/shop/auth/register", form);
      // Sync auth context (token + customer) via direct localStorage write.
      try { localStorage.setItem("shop_customer_token_v1", r.data.access_token); } catch (_) {}
      // Force a refresh of useShopAuth by hard-navigating (registerflow doet
      // dat sowieso). Eerst eerlijke toast over email-status.
      const status = r.data?.email_status;
      if (status === "sent") {
        toast.success("Account aangemaakt — controleer uw e-mail voor de verificatielink.");
      } else if (status === "skipped_no_key") {
        toast.warning("Account aangemaakt. De verificatiemail kon NIET worden verzonden (provider niet geconfigureerd). Neem contact op met support.");
      } else if (status === "failed") {
        toast.warning("Account aangemaakt, maar de verificatiemail kon niet worden verzonden. Klik op 'Verificatiemail opnieuw versturen' op uw dashboard.");
      } else {
        toast.success("Account aangemaakt.");
      }
      window.location.href = returnTo || "/shop-account";
    } catch (err) {
      setError(readDetail(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ShopAccountShell title="Account aanmaken" subtitle="Maak een 5999Cargo Shopaccount aan om uw bestellingen te volgen." testId="shop-register-page">
      <form onSubmit={submit} className="bg-white border border-gray-200 p-6 space-y-4 max-w-xl">
        <ErrorBanner message={error} />
        {prefillEmail && (
          <div className="bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900" data-testid="shop-reg-prefill-hint">
            Gebruik hetzelfde e-mailadres als bij uw bestelling om uw orders terug te vinden na verificatie.
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Voornaam *" testId="shop-reg-first-name" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} />
          <Field label="Achternaam *" testId="shop-reg-last-name" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} />
        </div>
        <Field label="E-mail *" type="email" testId="shop-reg-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} icon={Mail} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Telefoon" testId="shop-reg-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} icon={Phone} />
          <Field label="WhatsApp" testId="shop-reg-whatsapp" value={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} />
        </div>
        <Field label="Wachtwoord *" type="password" testId="shop-reg-password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} icon={Lock}
          hint="Minimaal 8 tekens, met minimaal 1 letter en 1 cijfer." />
        <Button type="submit" disabled={submitting} className="w-full bg-[#082c50] hover:bg-[#06223f] text-white rounded-xl font-chivo font-bold uppercase text-xs" data-testid="shop-reg-submit">
          {submitting ? "Bezig…" : "Account aanmaken"}
        </Button>
        <p className="text-xs text-gray-600 text-center">
          Al een account? <Link to="/shop-account/login" className="text-[#1a5193] underline font-bold">Inloggen</Link>
        </p>
      </form>
    </ShopAccountShell>
  );
}

// ============================================================
// Login
// ============================================================
export function LoginPage() {
  const { login, customer } = useShopAuth();
  const navigate = useNavigate();
  const [loginParams] = useSearchParams();
  const destination = loginParams.get("returnTo") === "/agent" ? "/agent" : "/shop-account";
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (customer) return <Navigate to={destination} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(form.email, form.password);
      toast.success("Welkom terug!");
      navigate(destination);
    } catch (err) {
      setError(readDetail(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ShopAccountShell title="Mijn 5999Cargo" subtitle="Log in op uw persoonlijke klantenomgeving." testId="shop-login-page">
      <form onSubmit={submit} className="bg-white border border-gray-200 p-6 space-y-4 max-w-md">
        <ErrorBanner message={error} />
        <Field label="E-mail" type="email" testId="shop-login-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} icon={Mail} />
        <Field label="Wachtwoord" type="password" testId="shop-login-password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} icon={Lock} />
        <Button type="submit" disabled={submitting} className="w-full bg-[#082c50] hover:bg-[#06223f] text-white rounded-xl font-chivo font-bold uppercase text-xs" data-testid="shop-login-submit">
          {submitting ? "Bezig…" : "Inloggen"}
        </Button>
        <div className="text-xs text-gray-600 flex justify-between">
          <Link to="/shop-account/forgot-password" className="underline" data-testid="shop-login-forgot">Wachtwoord vergeten?</Link>
          <Link to="/shop-account/register" className="text-[#1a5193] font-bold" data-testid="shop-login-register">Account aanmaken</Link>
        </div>
      </form>
      <section className="max-w-md mt-8 pt-6 border-t border-gray-200" data-testid="shop-login-activation-section">
        <h2 className="font-chivo font-bold text-base text-[#082c50] mb-2" data-testid="shop-login-existing-customer-title">Al klant bij 5999Cargo?</h2>
        <p className="text-sm text-gray-600 leading-6 mb-4" data-testid="shop-login-activation-description">Heeft u nog geen Mijn 5999Cargo-login? Activeer uw bestaande klantaccount.</p>
        <Link to="/shop-account/activate" className="inline-flex px-5 py-3 border border-[#082c50] rounded text-sm font-semibold text-[#082c50] hover:bg-blue-50" data-testid="shop-login-activate">Account activeren</Link>
        <p className="text-sm text-gray-600 mt-5" data-testid="shop-login-new-customer-description">Nog geen klant? <Link to="/shop-account/register" className="text-[#1a5193] underline" data-testid="shop-login-new-customer">Account aanmaken</Link></p>
      </section>
    </ShopAccountShell>
  );
}

// ============================================================
// Dashboard
// ============================================================
export function DashboardPage() {
  const { customer, logout, refresh } = useShopAuth();
  const [orders, setOrders] = useState(null);
  const [resending, setResending] = useState(false);
  const [communications, setCommunications] = useState(null);

  useEffect(() => {
    shopApi.get("/shop/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
    shopApi.get("/shop/communications")
      .then((r) => setCommunications(r.data?.items || []))
      .catch(() => setCommunications([]));
  }, []);

  if (!customer) return <Navigate to="/shop-account/login" replace />;

  const verified = !!customer.email_verified_at;

  const resendVerify = async () => {
    setResending(true);
    try {
      const r = await shopApi.post("/shop/auth/resend-verification");
      const status = r.data?.email_status;
      if (status === "already_verified") {
        toast.success("E-mailadres is al geverifieerd.");
        await refresh();
      } else if (status === "sent") {
        toast.success("Nieuwe verificatie-link verstuurd.");
      } else {
        toast.success(r.data?.message || "Verzonden.");
      }
    } catch (err) {
      toast.error(readDetail(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <ShopAccountShell title={`Hallo ${customer.first_name || customer.email}`} subtitle="Uw 5999Cargo-account." testId="shop-dashboard-page">
      <AccountAccessPanel />
      {!verified && (
        <div className="bg-amber-50 border border-amber-200 p-4 mb-6 flex items-start gap-3" data-testid="shop-dashboard-verify-banner">
          <ShieldAlert className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-bold text-amber-900 text-sm">Verifieer uw e-mailadres</p>
            <p className="text-xs text-amber-800 mt-1">Wij hebben een verificatie-link naar <strong>{customer.email}</strong> gestuurd. <strong>Na e-mailverificatie ziet u ook bestellingen die eerder met dit e-mailadres zijn geplaatst.</strong></p>
            <Button size="sm" variant="outline" onClick={resendVerify} disabled={resending} className="rounded-xl mt-2 text-xs" data-testid="shop-dashboard-resend-verify">
              {resending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />} Verificatiemail opnieuw versturen
            </Button>
          </div>
        </div>
      )}

      {/* Phase B-Polish — prominente CTA's */}
      <div className="bg-white border border-gray-200 p-5 mb-6" data-testid="shop-dashboard-quick-actions">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Link to="/webshop" className="border border-[#082c50] bg-[#082c50] text-white p-4 text-center hover:bg-[#06223f] transition-colors group" data-testid="shop-dashboard-to-webshop">
            <Package className="w-5 h-5 mx-auto mb-1.5 group-hover:scale-110 transition-transform" />
            <div className="font-chivo font-black uppercase text-xs">Naar 5999Cargo</div>
          </Link>
          <a href="#orders" className="border border-gray-300 bg-white text-[#082c50] p-4 text-center hover:bg-gray-50 transition-colors" data-testid="shop-dashboard-cta-orders">
            <Package className="w-5 h-5 mx-auto mb-1.5" />
            <div className="font-chivo font-black uppercase text-xs">Mijn bestellingen</div>
          </a>
          <Link to="/shop-account/profile" className="border border-gray-300 bg-white text-[#082c50] p-4 text-center hover:bg-gray-50 transition-colors" data-testid="shop-dashboard-cta-profile">
            <UserCog className="w-5 h-5 mx-auto mb-1.5" />
            <div className="font-chivo font-black uppercase text-xs">Mijn gegevens</div>
          </Link>
          <Link to="/shop-account/profile" className="border border-gray-300 bg-white text-[#082c50] p-4 text-center hover:bg-gray-50 transition-colors" data-testid="shop-dashboard-cta-addresses">
            <MapPin className="w-5 h-5 mx-auto mb-1.5" />
            <div className="font-chivo font-black uppercase text-xs">Adresboek</div>
          </Link>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-gray-200 p-5">
          <div className="text-[11px] uppercase font-bold text-gray-500 mb-2">Profiel</div>
          <p className="text-sm font-bold">{customer.first_name} {customer.last_name}</p>
          <p className="text-xs text-gray-600 break-all">{customer.email}</p>
          <p className="text-xs text-gray-600">{customer.phone || "—"}</p>
          <Link to="/shop-account/profile" className="text-[11px] text-[#1a5193] underline mt-2 inline-block" data-testid="shop-dashboard-profile-link">Profiel bewerken →</Link>
        </div>
        <div className="bg-white border border-gray-200 p-5">
          <div className="text-[11px] uppercase font-bold text-gray-500 mb-2">Status</div>
          <div className="flex items-center gap-1.5 text-sm font-bold">
            {verified ? <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Geverifieerd</>
                     : <><ShieldAlert className="w-4 h-4 text-amber-600" /> Niet geverifieerd</>}
          </div>
          <p className="text-xs text-gray-500 mt-2">Lid sinds {customer.created_at ? new Date(customer.created_at).toLocaleDateString("nl-NL") : "—"}</p>
        </div>
        <div className="bg-white border border-gray-200 p-5 flex flex-col justify-between">
          <div>
            <div className="text-[11px] uppercase font-bold text-gray-500 mb-2">Acties</div>
            <Button size="sm" variant="outline" onClick={logout} className="rounded-xl text-xs w-full" data-testid="shop-dashboard-logout">
              <LogOut className="w-3 h-3 mr-1" /> Uitloggen
            </Button>
          </div>
        </div>
      </div>

      <div id="orders" className="bg-white border border-gray-200 p-5">
        <h2 className="font-chivo font-black text-lg text-[#082c50] mb-4 flex items-center gap-2">
          <Package className="w-5 h-5" /> Mijn bestellingen
        </h2>
        {orders === null ? (
          <p className="text-sm text-gray-500"><Loader2 className="w-3 h-3 inline animate-spin" /> Laden…</p>
        ) : orders.length === 0 ? (
          <div className="text-sm text-gray-500 py-6 text-center border border-dashed border-gray-300" data-testid="shop-dashboard-orders-empty">
            {verified
              ? "U heeft nog geen bestellingen geplaatst. Bekijk onze webshop voor verhuisdozen en accessoires."
              : "Bestellingen worden zichtbaar zodra u uw e-mail heeft geverifieerd."}
          </div>
        ) : (
          <ul className="divide-y divide-gray-200" data-testid="shop-dashboard-orders-list">
            {orders.map((o) => (
              <li key={o.id} className="py-3 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm">Order {o.id.slice(0, 8)}</p>
                  <p className="text-xs text-gray-500">{o.created_at ? new Date(o.created_at).toLocaleString("nl-NL") : ""}</p>
                  <p className="text-xs text-gray-700 mt-1">{(o.items || []).length} regel(s) · {fmtEur(o.amount_total)} · {o.status}</p>
                </div>
                <Link to={`/shop-account/orders/${o.id}`} className="text-xs text-[#1a5193] font-bold flex items-center" data-testid={`shop-order-link-${o.id}`}>
                  Bekijk <ChevronRight className="w-3 h-3" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Enterprise Communicatiecentrum · klantportaal Communicatie tab */}
      <div id="communications" className="bg-white border border-gray-200 p-5 mt-6" data-testid="shop-dashboard-communications">
        <h2 className="font-chivo font-black text-lg text-[#082c50] mb-4 flex items-center gap-2">
          <MessageSquare className="w-5 h-5" /> Communicatie met 5999Cargo
        </h2>
        {!verified ? (
          <div className="text-sm text-gray-500 py-6 text-center border border-dashed border-gray-300">
            Uw communicatiehistorie wordt zichtbaar zodra u uw e-mail heeft geverifieerd.
          </div>
        ) : communications === null ? (
          <p className="text-sm text-gray-500"><Loader2 className="w-3 h-3 inline animate-spin" /> Laden…</p>
        ) : communications.length === 0 ? (
          <div className="text-sm text-gray-500 py-6 text-center border border-dashed border-gray-300" data-testid="shop-dashboard-comms-empty">
            Nog geen communicatie op dit e-mailadres. Zodra u een factuur, herinnering of betaalbevestiging ontvangt, verschijnt deze hier.
          </div>
        ) : (
          <ul className="divide-y divide-gray-200" data-testid="shop-dashboard-comms-list">
            {communications.slice(0, 20).map((c) => (
              <li key={`${c.type}-${c.id}`} className="py-3 flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  c.type === "reminder" ? "bg-amber-50 text-amber-700" :
                  c.type === "stripe" ? "bg-blue-50 text-blue-700" :
                  "bg-gray-50 text-gray-700"
                }`}>
                  {c.type === "reminder" ? <Bell className="w-4 h-4" /> :
                   c.type === "stripe" ? <CreditCard className="w-4 h-4" /> :
                   <Mail className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate">{c.subject || c.stripe_event_type || c.email_type || "—"}</p>
                  <p className="text-[11px] text-gray-500">
                    {c.occurred_at ? new Date(c.occurred_at).toLocaleString("nl-NL") : ""}
                    {c.invoice_number && <span> · Factuur {c.invoice_number}</span>}
                    {c.amount != null && <span> · {new Intl.NumberFormat("nl-NL", {style: "currency", currency: c.currency || "EUR"}).format(c.amount)}</span>}
                  </p>
                </div>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700 flex-shrink-0">
                  {c.type === "reminder" ? "Herinnering" : c.type === "stripe" ? "Betaling" : "E-mail"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </ShopAccountShell>
  );
}

// ============================================================
// OrderDetail
// ============================================================
export function OrderDetailPage() {
  const { id } = useParams();
  const { customer } = useShopAuth();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    shopApi.get(`/shop/orders/${id}`)
      .then((r) => setOrder(r.data))
      .catch((err) => setError(readDetail(err)));
  }, [id]);

  if (!customer) return <Navigate to="/shop-account/login" replace />;
  if (error) return (
    <ShopAccountShell title="Bestelling niet gevonden" testId="shop-order-detail-page">
      <ErrorBanner message={error} />
      <Link to="/shop-account" className="text-xs text-[#1a5193] underline">← Terug naar overzicht</Link>
    </ShopAccountShell>
  );
  if (!order) return (
    <ShopAccountShell title="Laden..." testId="shop-order-detail-page">
      <Loader2 className="w-4 h-4 animate-spin" />
    </ShopAccountShell>
  );

  return (
    <ShopAccountShell title={`Order ${order.id.slice(0, 8)}`} subtitle={order.created_at ? new Date(order.created_at).toLocaleString("nl-NL") : ""} testId="shop-order-detail-page">
      <Link to="/shop-account" className="text-xs text-[#1a5193] underline">← Terug naar overzicht</Link>
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <Card title="Status" testid="shop-order-status-card">
          <div className="text-sm"><strong>Order:</strong> {order.status}</div>
          <div className="text-sm"><strong>Betaling:</strong> {order.payment_status}</div>
          <div className="text-sm"><strong>Totaal:</strong> {fmtEur(order.amount_total)}</div>
        </Card>
        <Card title="Klant" testid="shop-order-customer-card">
          <div className="text-sm">{order.customer?.first_name || order.customer?.name} {order.customer?.last_name || ""}</div>
          <div className="text-xs text-gray-600 break-all">{order.customer?.email}</div>
          <div className="text-xs text-gray-600">{order.customer?.phone}</div>
        </Card>
        <Card title="Bezorgadres" testid="shop-order-shipping-card">
          {order.shipping_address ? (
            <>
              <div className="text-sm">{order.shipping_address.street} {order.shipping_address.house_number}{order.shipping_address.house_number_addition ? ` ${order.shipping_address.house_number_addition}` : ""}</div>
              <div className="text-sm">{order.shipping_address.postal_code} {order.shipping_address.city}</div>
              <div className="text-sm">{order.shipping_address.country}</div>
              {order.shipping_address.delivery_note && <div className="text-xs italic text-gray-600 mt-2">{order.shipping_address.delivery_note}</div>}
            </>
          ) : <div className="text-xs text-gray-500 italic">Geen bezorgadres opgeslagen.</div>}
        </Card>
        <Card title="Factuuradres" testid="shop-order-billing-card">
          {order.billing_address?.same_as_shipping ? (
            <div className="text-xs text-gray-500 italic">Hetzelfde als bezorgadres</div>
          ) : order.billing_address ? (
            <>
              {order.billing_address.company_name && <div className="text-sm font-bold">{order.billing_address.company_name}</div>}
              <div className="text-sm">{order.billing_address.street} {order.billing_address.house_number}</div>
              <div className="text-sm">{order.billing_address.postal_code} {order.billing_address.city}</div>
              <div className="text-sm">{order.billing_address.country}</div>
            </>
          ) : <div className="text-xs text-gray-500 italic">Geen factuuradres opgeslagen.</div>}
        </Card>
      </div>

      <Card title="Bestelregels" testid="shop-order-items-card" className="mt-4">
        <ul className="divide-y divide-gray-200">
          {(order.items || []).map((it, i) => (
            <li key={i} className="py-2 flex justify-between text-sm">
              <span>{it.product_name}{it.variation_name ? ` — ${it.variation_name}` : ""} × {it.quantity}</span>
              <span className="font-bold">{fmtEur((it.line_total_cents || 0) / 100)}</span>
            </li>
          ))}
        </ul>
        <div className="mt-3 pt-3 border-t border-gray-200 text-sm space-y-1">
          {order.amount_subtotal_cents !== undefined && (
            <div className="flex justify-between"><span>Subtotaal</span><span>{fmtEur((order.amount_subtotal_cents || 0)/100)}</span></div>
          )}
          {order.discount && order.discount.discount_cents > 0 && (
            <div className="flex justify-between text-emerald-700"><span>Korting ({order.discount.code})</span><span>− {fmtEur(order.discount.discount_cents/100)}</span></div>
          )}
          {order.shipping_cents > 0 && (
            <div className="flex justify-between"><span>Verzending{order.shipping?.name ? ` — ${order.shipping.name}` : ""}</span><span>{fmtEur(order.shipping_cents/100)}</span></div>
          )}
          <div className="flex justify-between font-chivo font-black text-base pt-1 border-t border-gray-100 text-[#082c50]"><span>Totaal</span><span data-testid="shop-order-total">{fmtEur(order.amount_total)}</span></div>
        </div>
      </Card>
    </ShopAccountShell>
  );
}

// ============================================================
// Profile
// ============================================================
export function ProfilePage() {
  const { customer, refresh } = useShopAuth();
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", whatsapp: "" });
  const [shipping, setShipping] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (customer) {
      setForm({
        first_name: customer.first_name || "",
        last_name: customer.last_name || "",
        phone: customer.phone || "",
        whatsapp: customer.whatsapp || "",
      });
      setShipping(customer.default_shipping_address || null);
    }
  }, [customer]);

  if (!customer) return <Navigate to="/shop-account/login" replace />;

  const save = async () => {
    setSubmitting(true);
    try {
      await shopApi.patch("/shop/profile", form);
      toast.success("Profiel bijgewerkt");
      await refresh();
    } catch (err) {
      toast.error(readDetail(err));
    } finally { setSubmitting(false); }
  };

  return (
    <ShopAccountShell title="Mijn gegevens" subtitle="Beheer uw gegevens en adresboek." testId="shop-profile-page">
      <Link to="/shop-account" className="text-xs text-[#1a5193] underline">← Terug naar dashboard</Link>
      <div className="bg-white border border-gray-200 p-6 mt-4 max-w-xl">
        <h2 className="font-chivo font-black text-lg text-[#082c50] mb-4">Persoonsgegevens</h2>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Voornaam" testId="shop-profile-first-name" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} />
            <Field label="Achternaam" testId="shop-profile-last-name" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} />
          </div>
          <Field label="E-mail (kan niet gewijzigd worden in deze versie)" value={customer.email} onChange={() => {}} testId="shop-profile-email-readonly" disabled />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Telefoon" testId="shop-profile-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} icon={Phone} />
            <Field label="WhatsApp" testId="shop-profile-whatsapp" value={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} />
          </div>
          <Button onClick={save} disabled={submitting} className="bg-[#082c50] hover:bg-[#06223f] text-white rounded-xl font-chivo font-bold uppercase text-xs" data-testid="shop-profile-save">
            {submitting ? "Bezig…" : "Wijzigingen opslaan"}
          </Button>
        </div>
      </div>

      <AddressBookCard
        title="Standaard bezorgadres"
        testidPrefix="shop-profile-ship"
        value={shipping}
        onSave={async (addr) => {
          await shopApi.put("/shop/profile/default-shipping", addr);
          toast.success("Bezorgadres opgeslagen");
          await refresh();
        }}
        onClear={async () => {
          await shopApi.delete("/shop/profile/default-shipping");
          toast.success("Bezorgadres gewist");
          await refresh();
        }}
      />
    </ShopAccountShell>
  );
}

export function AddressBookCard({ title, testidPrefix, value, onSave, onClear }) {
  const [edit, setEdit] = useState(!value);
  const [a, setA] = useState(value || {
    street: "", house_number: "", house_number_addition: "",
    postal_code: "", city: "", country: "Nederland",
  });
  const { status, result } = usePostcodeLookup({
    postal_code: a.postal_code, house_number: a.house_number, country: a.country,
    enabled: edit, variant: "public",
  });
  useEffect(() => {
    if (status === "found" && result) {
      setA((cur) => ({
        ...cur,
        street: cur.street || result.street || "",
        city: cur.city || result.city || "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, result]);

  useEffect(() => { if (value) setA(value); }, [value]);

  if (!edit && value) {
    return (
      <div className="bg-white border border-gray-200 p-6 mt-4 max-w-xl" data-testid={`${testidPrefix}-card`}>
        <h2 className="font-chivo font-black text-lg text-[#082c50] mb-3">{title}</h2>
        <div className="text-sm">
          <div>{value.street} {value.house_number}{value.house_number_addition ? ` ${value.house_number_addition}` : ""}</div>
          <div>{value.postal_code} {value.city}</div>
          <div>{value.country}</div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button size="sm" variant="outline" onClick={() => setEdit(true)} className="rounded-xl text-xs" data-testid={`${testidPrefix}-edit`}>Wijzigen</Button>
          <Button size="sm" variant="ghost" onClick={onClear} className="rounded-xl text-xs text-red-600" data-testid={`${testidPrefix}-clear`}>Wissen</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 p-6 mt-4 max-w-xl" data-testid={`${testidPrefix}-card`}>
      <h2 className="font-chivo font-black text-lg text-[#082c50] mb-3">{title}</h2>
      <div className="grid grid-cols-2 gap-2.5">
        <Field label="Postcode" testId={`${testidPrefix}-postal-code`} value={a.postal_code} onChange={(v) => setA({ ...a, postal_code: v })} />
        <Field label="Huisnummer" testId={`${testidPrefix}-house-number`} value={a.house_number} onChange={(v) => setA({ ...a, house_number: v })} />
        <Field label="Toevoeging" testId={`${testidPrefix}-house-number-addition`} value={a.house_number_addition} onChange={(v) => setA({ ...a, house_number_addition: v })} />
        <Field label="Land" testId={`${testidPrefix}-country`} value={a.country} onChange={(v) => setA({ ...a, country: v })} />
        <Field label="Straat" testId={`${testidPrefix}-street`} value={a.street} onChange={(v) => setA({ ...a, street: v })} />
        <Field label="Stad" testId={`${testidPrefix}-city`} value={a.city} onChange={(v) => setA({ ...a, city: v })} />
      </div>
      {status === "found" && (
        <p className="text-xs text-emerald-700 mt-2"><CheckCircle2 className="w-3 h-3 inline" /> Adres gevonden</p>
      )}
      <div className="flex gap-2 mt-3">
        <Button onClick={async () => { try { await onSave(a); setEdit(false); } catch (_) {} }} className="bg-[#082c50] hover:bg-[#06223f] text-white rounded-xl font-chivo font-bold uppercase text-xs" data-testid={`${testidPrefix}-save`}>Opslaan</Button>
        {value && <Button variant="outline" onClick={() => { setA(value); setEdit(false); }} className="rounded-xl text-xs" data-testid={`${testidPrefix}-cancel`}>Annuleren</Button>}
      </div>
    </div>
  );
}

// ============================================================
// Verify Email landingspagina (deep-link uit mail)
// ============================================================
export function VerifyEmailPage() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const submittedToken = useRef(null);
  const [state, setState] = useState({ loading: true, ok: false, message: "", linked: 0 });
  const { refresh, customer } = useShopAuth();

  useEffect(() => {
    if (!token) {
      setState({ loading: false, ok: false, message: "Geen verificatie-token meegegeven in de link." });
      return;
    }
    // A single-use token must not be submitted twice by React effect replay.
    if (submittedToken.current === token) return;
    submittedToken.current = token;
    shopApi.post("/shop/auth/verify-email", { token })
      .then(async (r) => {
        setState({ loading: false, ok: true, message: r.data.message || "E-mailadres geverifieerd!", linked: r.data.linked_orders || 0 });
        if (customer) await refresh();
      })
      .catch((err) => setState({ loading: false, ok: false, message: readDetail(err) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <ShopAccountShell title="E-mail verifiëren" testId="shop-verify-page">
      <div className="bg-white border border-gray-200 p-6 max-w-xl">
        {state.loading ? (
          <p className="text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline" /> Verifiëren...</p>
        ) : state.ok ? (
          <div data-testid="shop-verify-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mb-3" />
            <h2 className="font-chivo font-black text-xl text-[#082c50] mb-2">Gelukt!</h2>
            <p className="text-sm text-gray-700">{state.message}</p>
            {state.linked > 0 && (
              <p className="text-sm text-emerald-700 mt-2"><strong>{state.linked}</strong> historische bestelling(en) zijn toegevoegd aan uw account.</p>
            )}
            <Link to="/shop-account" className="inline-block mt-4 bg-[#082c50] hover:bg-[#06223f] text-white rounded-xl font-chivo font-bold uppercase text-xs px-4 py-2" data-testid="shop-verify-to-dashboard">
              Naar Mijn 5999Cargo
            </Link>
          </div>
        ) : (
          <div data-testid="shop-verify-error">
            <AlertCircle className="w-10 h-10 text-red-600 mb-3" />
            <h2 className="font-chivo font-black text-xl text-[#082c50] mb-2">Verificatie mislukt</h2>
            <p className="text-sm text-gray-700">{state.message}</p>
            <Link to="/shop-account/login" className="text-xs text-[#1a5193] underline mt-3 inline-block">← Inloggen</Link>
          </div>
        )}
      </div>
    </ShopAccountShell>
  );
}

// ============================================================
// Forgot + Reset
// ============================================================
export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await shopApi.post("/shop/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (err) {
      setError(readDetail(err));
    } finally { setSubmitting(false); }
  };

  return (
    <ShopAccountShell title="Wachtwoord vergeten" subtitle="Vul uw e-mail in — we sturen u een reset-link." testId="shop-forgot-page">
      <form onSubmit={submit} className="bg-white border border-gray-200 p-6 space-y-4 max-w-md">
        <ErrorBanner message={error} />
        {submitted ? (
          <p className="text-sm text-emerald-700" data-testid="shop-forgot-success">
            Als er een account bestaat met dit e-mailadres, is er een reset-link verstuurd.
          </p>
        ) : (
          <>
            <Field label="E-mail" type="email" testId="shop-forgot-email" value={email} onChange={setEmail} icon={Mail} />
            <Button type="submit" disabled={submitting} className="w-full bg-[#082c50] hover:bg-[#06223f] text-white rounded-xl font-chivo font-bold uppercase text-xs" data-testid="shop-forgot-submit">
              {submitting ? "Bezig…" : "Reset-link sturen"}
            </Button>
          </>
        )}
        <Link to="/shop-account/login" className="text-xs text-[#1a5193] underline block text-center">← Terug naar inloggen</Link>
      </form>
    </ShopAccountShell>
  );
}

export function ResetPasswordPage() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== pw2) return setError("Wachtwoorden komen niet overeen.");
    if (pw.length < 8 || !/[A-Za-z]/.test(pw) || !/\d/.test(pw))
      return setError("Wachtwoord moet minimaal 8 tekens hebben met minimaal 1 letter en 1 cijfer.");
    setSubmitting(true);
    try {
      await shopApi.post("/shop/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (err) {
      setError(readDetail(err));
    } finally { setSubmitting(false); }
  };

  return (
    <ShopAccountShell title="Wachtwoord opnieuw instellen" testId="shop-reset-page">
      <form onSubmit={submit} className="bg-white border border-gray-200 p-6 space-y-4 max-w-md">
        <ErrorBanner message={error} />
        {done ? (
          <div data-testid="shop-reset-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mb-2" />
            <p className="text-sm text-gray-700">Wachtwoord is gewijzigd. <Link to="/shop-account/login" className="text-[#1a5193] underline">Inloggen</Link></p>
          </div>
        ) : (
          <>
            <Field label="Nieuw wachtwoord" type="password" testId="shop-reset-pw" value={pw} onChange={setPw} icon={Lock} />
            <Field label="Bevestig wachtwoord" type="password" testId="shop-reset-pw2" value={pw2} onChange={setPw2} icon={Lock} />
            <Button type="submit" disabled={submitting} className="w-full bg-[#082c50] hover:bg-[#06223f] text-white rounded-xl font-chivo font-bold uppercase text-xs" data-testid="shop-reset-submit">
              {submitting ? "Bezig…" : "Wachtwoord wijzigen"}
            </Button>
          </>
        )}
      </form>
    </ShopAccountShell>
  );
}

// ============================================================
// Helpers
// ============================================================
function Field({ label, type = "text", value, onChange, testId, icon: Icon, hint, disabled = false }) {
  return (
    <div>
      <Label className="text-[11px] uppercase font-bold">{label}</Label>
      <div className="relative mt-1">
        {Icon && <Icon className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />}
        <Input
          type={type}
          data-testid={testId}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={`rounded-xl ${Icon ? "pl-8" : ""} ${disabled ? "bg-gray-50 text-gray-500" : ""}`}
        />
      </div>
      {hint && <p className="text-[10px] text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}

function Card({ title, children, testid, className = "" }) {
  return (
    <div className={`bg-white border border-gray-200 p-5 ${className}`} data-testid={testid}>
      <div className="text-[11px] uppercase font-bold text-gray-500 mb-2">{title}</div>
      {children}
    </div>
  );
}
